import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { BeaconMedia } from "../pages/BeaconMedia";
import { ArtLightbox } from "./ArtLightbox";
import { ShareArtButton } from "./ShareArtButton";

// Storefront context for artwork pieces: who owns the Beacon and where it lives.
export const ArtCtx = createContext({});

export const InterestedButton = ({ it, art, accent }) => {
  const { ownerId, isOwner, handle, teaserOn } = useContext(ArtCtx);
  const navigate = useNavigate();
  const share = teaserOn && it.is_public ? <ShareArtButton handle={handle} it={it} accent={accent} /> : null;
  if (!ownerId || !art || art.inquire === false) return share;
  const msg = `Hi, I'm interested in "${it.title || "this piece"}"${art.year ? ` (${art.year})` : ""}. ${window.location.origin}/beacon/view/${handle}?lesson=${it.id}`;
  if (isOwner) return <span className="inline-flex flex-wrap gap-2"><span className="inline-flex items-center gap-1.5 border border-dashed px-3 py-1.5 font-body text-xs opacity-70" style={{ borderColor: accent, color: accent }} data-testid="art-interested-preview"><MessageSquare size={13} /> Interested in this piece? (members see this)</span>{share}</span>;
  return (
    <span className="inline-flex flex-wrap gap-2">
    <button type="button" onClick={() => navigate(`/messages/${ownerId}?draft=${encodeURIComponent(msg)}`)} data-testid={`art-interested-${it.id}`}
      className="inline-flex items-center gap-1.5 border px-3 py-1.5 font-body text-xs hover:brightness-125 transition" style={{ borderColor: accent, color: accent }}>
      <MessageSquare size={13} /> Interested in this piece?
    </button>
    {share}
    </span>
  );
};

// Optional story extras: written story, voice note, studio video and process images.
export const ArtStory = ({ it, art, encKey, accent }) => {
  const [open, setOpen] = useState(null);
  const x = it.extras || {};
  const key = it.enc ? encKey : null;
  const process = (x.process || []).map((m, i) => ({ id: `${it.id}-p${i}`, media: m.path, media_iv: m.iv, enc: it.enc, _plain: true }));
  if (!art?.story && !x.voice && !x.video && !process.length) return null;
  return (
    <div className="space-y-3 mt-3" data-testid={`art-story-${it.id}`}>
      {art?.story && <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid="art-story-text">{art.story}</p>}
      {x.voice && <div><span className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500">Voice note</span><BeaconMedia path={x.voice.path} type="audio" encKey={key} iv={x.voice.iv} /></div>}
      {x.video && <div><span className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500">From the studio</span><BeaconMedia path={x.video.path} type="video" encKey={key} iv={x.video.iv} className="w-full max-h-[360px] rounded mt-1" /></div>}
      {process.length > 0 && (
        <div>
          <span className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500">Process</span>
          <div className="flex gap-2 overflow-x-auto mt-1 pb-1" data-testid="art-process-strip">
            {process.map((p, i) => (
              <button key={p.id} type="button" onClick={() => setOpen(i)} className="shrink-0 w-20 h-20 overflow-hidden bg-white/5 focus:outline-none" aria-label="Open process image">
                <BeaconMedia path={p.media} type="image" encKey={key} iv={p.media_iv} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
      {open !== null && <ArtLightbox items={process} index={open} encKey={encKey} accent={accent} onClose={() => setOpen(null)} />}
    </div>
  );
};
